import React, { useEffect, useState } from 'react';
import './MyBotHistory.scss';
import { Container, Col, Card, Row, Button, Badge } from 'react-bootstrap';
import { connect } from 'react-redux';
import {
  getBotRequestAction,
  getRegisteredBotRequestAction,
  registerRequest,
  resetLoadingStatesAction
} from '../../store/bot/botActions';
import { setLoadingAction, setLoadingFullAction } from '../../store/loading/loadingActions';
import { useHistory, useLocation } from 'react-router-dom';
import {
  accountDetailsRequestAction
} from '../../store/account/userActions';
import bnbusdt from '../../assets/img/coin/bnbusdt.png';
import okxusdt from '../../assets/img/coin/okxusdt.png';

// Import modern icons
import { 
  Bot,
  TrendingUp,
  TrendingDown,
  Calendar,
  ShieldAlert,
  User,
  Activity,
  BarChart3,
  Settings,
  ExternalLink,
  Plus,
  ShoppingBag,
  CheckCircle,
  XCircle
} from 'lucide-react';

function MyBotHistory({ ...props }) {
  const {
    botData,
    registeredBotData,
    loadingStates,
    apiCompletionStates,
    getBotRequest,
    getRegisteredBotRequest,
    setLoadingFull,
    registerRequest,
    user,
    accountDetailsRequest,
    resetLoadingStates
  } = props;

  const history = useHistory();
  const location = useLocation();
  const { exchanges } = location.state || {};

  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [hasDataLoaded, setHasDataLoaded] = useState(false);
  const [isComponentMounted, setIsComponentMounted] = useState(false);

  useEffect(() => {

    setIsComponentMounted(true);
    setIsInitialLoading(true);
    setHasDataLoaded(false);
    
    setLoadingFull({ isLoading: true, loadingText: 'Loading Bots...' });
    resetLoadingStates();
    
    setTimeout(() => {
      getBotRequest();
      getRegisteredBotRequest();
      accountDetailsRequest();
    }, 100);

    const failsafeTimeout = setTimeout(() => {
      console.warn('Loading timeout reached - forcing completion');
      setIsInitialLoading(false);
      setLoadingFull({ isLoading: false, loadingText: '' });
    }, 8000);

    return () => {
      clearTimeout(failsafeTimeout);
    };
  }, []);

  useEffect(() => {
    if (!isComponentMounted) return;
    
    const { botData: botDataComplete, 
      registeredBotData: registeredBotComplete } = apiCompletionStates;
    
    
    if (botDataComplete && registeredBotComplete) {
      setIsInitialLoading(false);
      setHasDataLoaded(true);
      setLoadingFull({ isLoading: false, loadingText: '' });
    }
  }, [apiCompletionStates, setLoadingFull, isComponentMounted]);

  useEffect(() => {
    if (!isComponentMounted) return;
    
    if (!isInitialLoading && 
        (botData !== null && botData !== undefined) && 
        (registeredBotData !== null && registeredBotData !== undefined)) {
      setHasDataLoaded(true);
      setLoadingFull({ isLoading: false, loadingText: '' });
    }
  }, [botData, registeredBotData, isInitialLoading, setLoadingFull, isComponentMounted]);

  useEffect(() => {
    if (!isInitialLoading) {
      const gracefulTimeout = setTimeout(() => {
        setLoadingFull({ isLoading: false, loadingText: '' });
      }, 500);

      return () => clearTimeout(gracefulTimeout);
    }
  }, [isInitialLoading, setLoadingFull]);

  const botDataArray = React.useMemo(() => {
    if (!registeredBotData || typeof registeredBotData !== "object") {
      return [];
    }

    return Object.values(registeredBotData)
      .map(bot => {
        if (botData?.[bot.bot_id - 1]) {
          return {
            ...botData[bot.bot_id - 1],
            start_date: bot.start_date,
            bot_pnl: bot.pnl,
            registered_exchange: bot.exchange
          };
        }
        return null;
      })
      .filter(item => item !== null);
  }, [botData, registeredBotData]);

  const isAnyLoading = React.useMemo(() => {
    const loading = isInitialLoading || 
           loadingStates.botData || 
           loadingStates.registeredBotData ||
           (!hasDataLoaded && !apiCompletionStates.botData &&
             !apiCompletionStates.registeredBotData);
    
    return loading;
  }, [isInitialLoading, loadingStates, hasDataLoaded, apiCompletionStates]);

  const routeStrategie = async (e, id) => {
    history.push(`/general-settings/bot-management/${id}`);
  };

  const redirectApiKey = async () => {
    history.push('/general-settings/api-settings');
  };

  const handleCardClick = (id, exchanges, name) => {
    history.push({
      pathname: `/bot-detail/${id}`,
      state: { exchanges, name }
    });
  };

  const redirectToMarketplace = () => {
    history.push('/marketplace');
  };

  // Calculate statistics
  const totalBots = botDataArray.length;
  const activeBots = botDataArray.filter(bot => bot.status).length;
  const totalPnL = botDataArray.reduce((sum, bot) => sum + (bot.bot_pnl || 0), 0);
  const averagePnL = totalBots > 0 ? totalPnL / totalBots : 0;

  // Modern Stats Card Component
  const StatsCard = ({ icon: Icon, title, value, subtitle, color = 'primary', trend }) => (
    <Card className="modern-stats-card">
      <Card.Body className="stats-card-body">
        <div className={`stats-icon stats-icon-${color}`}>
          <Icon size={24} />
        </div>
        <div className="stats-content">
          <h6 className="stats-title">{title}</h6>
          <h4 className="stats-value">{value}</h4>
          {subtitle && <small className="stats-subtitle">{subtitle}</small>}
          {trend !== undefined && (
            <div className="stats-trend">
              {trend > 0 ? (
                <TrendingUp size={16} className="trend-up" />
              ) : trend < 0 ? (
                <TrendingDown size={16} className="trend-down" />
              ) : null}
              {trend !== 0 && (
                <small className={trend > 0 ? 'trend-up' : 'trend-down'}>
                  {Math.abs(trend).toFixed(2)}%
                </small>
              )}
            </div>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  // Modern Bot Card Component
  const ModernBotCard = ({ bot, index }) => {
    const pnlValue = bot.bot_pnl || 0;
    const isPositivePnL = pnlValue >= 0;
    const daysSinceStart = Math.floor(
      (new Date() - new Date(bot.start_date)) / (1000 * 60 * 60 * 24));

    return (
      <Card 
        className="modern-bot-card"
        onClick={() => handleCardClick(bot.id, bot.exchanges, bot.name)}
      >
        {/* Card Header */}
        <div className="bot-card-header">
          <div className="bot-title-section">
            <h5 className="bot-name">{bot.name}</h5>
            <div className="bot-status">
              {bot.status ? (
                <Badge bg="success" className="status-badge">
                  <CheckCircle size={12} />
                  Active
                </Badge>
              ) : (
                <Badge bg="secondary" className="status-badge">
                  <XCircle size={12} />
                  Inactive
                </Badge>
              )}
            </div>
          </div>
          
          <div className="bot-actions">
            <Button
              variant="outline-primary"
              size="sm"
              className="manage-btn"
              onClick={(e) => {
                e.stopPropagation();
                routeStrategie(e, bot.id);
              }}
            >
              <Settings size={14} />
            </Button>
          </div>
        </div>

        {/* Bot Icon and Performance */}
        <div className="bot-performance-section">
          <div className="bot-icon-wrapper">
            <div className={`bot-icon ${bot.status ? 'active' : 'inactive'}`}>
              <Bot size={32} />
            </div>
            <div className="performance-indicator">
              <div className={`pnl-value ${isPositivePnL ? 'positive' : 'negative'}`}>
                {isPositivePnL ? '+' : ''}{pnlValue.toFixed(2)}%
              </div>
              <div className="pnl-label">P&L</div>
            </div>
          </div>
        </div>

        {/* Bot Details */}
        <div className="bot-details">
          <div className="detail-row">
            <div className="detail-item">
              <Activity size={16} className="detail-icon" />
              <span className="detail-label">Exchange:</span>
              <div className="exchange-info">
                <span className="exchange-name">
                  {bot.registered_exchange.charAt(0).toUpperCase() + 
                   bot.registered_exchange.slice(1).toLowerCase()}
                </span>
                <img
                  src={bot.registered_exchange === 'binance' ? bnbusdt : okxusdt}
                  width={20}
                  height={20}
                  alt={bot.registered_exchange}
                  className="exchange-logo"
                />
              </div>
            </div>
          </div>

          <div className="detail-row">
            <div className="detail-item">
              <User size={16} className="detail-icon" />
              <span className="detail-label">Creator:</span>
              <span className="detail-value">{bot.creator}</span>
            </div>
          </div>

          <div className="detail-row">
            <div className="detail-item">
              <Calendar size={16} className="detail-icon" />
              <span className="detail-label">Started:</span>
              <div className="date-info">
                <span className="detail-value">
                  {new Date(bot.start_date).toLocaleDateString()}
                </span>
                <Badge bg="light" text="dark" className="days-badge">
                  {daysSinceStart} days ago
                </Badge>
              </div>
            </div>
          </div>

          <div className="detail-row">
            <div className="detail-item">
              <ShieldAlert size={16} className="detail-icon" />
              <span className="detail-label">Risk:</span>
              <div className="date-info">
                <span className="detail-value">
                  {bot.risk}
                </span>
              </div>
            </div>
          </div>

          <div className="detail-row">
            <div className="detail-item">
              <BarChart3 size={16} className="detail-icon" />
              <span className="detail-label">Performance:</span>
              <div className={`performance-badge ${isPositivePnL ? 'positive' : 'negative'}`}>
                {isPositivePnL ? (
                  <TrendingUp size={14} />
                ) : (
                  <TrendingDown size={14} />
                )}
                <span>{isPositivePnL ? 'Profitable' : 'Loss'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Card Footer */}
        <div className="bot-card-footer">
          <Button
            variant="primary"
            size="sm"
            className="view-details-btn"
            onClick={() => handleCardClick(bot.id, bot.exchanges, bot.name)}
          >
            <ExternalLink size={14} />
            View Details
          </Button>
        </div>
      </Card>
    );
  };

  // Empty State Component
  const EmptyState = () => (
    <div className="empty-state">
      <div className="empty-state-icon">
        <Bot size={80} />
      </div>
      <h3 className="empty-state-title">No Trading Bots Yet</h3>
      <p className="empty-state-description">
        Start your automated trading journey by registering your first bot from our marketplace.
      </p>
      <div className="empty-state-actions">
        <Button
          variant="primary"
          size="lg"
          className="marketplace-btn"
          onClick={redirectToMarketplace}
        >
          <ShoppingBag size={20} />
          Explore Marketplace
        </Button>
        <Button
          variant="outline-secondary"
          size="lg"
          className="api-btn"
          onClick={redirectApiKey}
        >
          <Settings size={20} />
          Configure API
        </Button>
      </div>
    </div>
  );

  return (
    <div className="modern-my-bots">
      <Container>
        {/* Header Section */}
        <div className="my-bots-header">
          <div className="header-content">
            <div className="header-left">
              <h1 className="page-title">
                <Bot size={32} className="title-icon" />
                {botDataArray.length === 1 ? "My Bot" : "My Bots"}
              </h1>
              <p className="page-subtitle">
                Monitor and manage your automated trading strategies
              </p>
            </div>
            {botDataArray.length > 0 && (
              <div className="header-actions">
                <Button
                  variant="outline-primary"
                  onClick={redirectToMarketplace}
                  className="add-bot-btn"
                >
                  <Plus size={16} />
                  Add Bot
                </Button>
              </div>
            )}
          </div>
        </div>

        {botDataArray.length > 0 ? (
          <>
            {/* Bots Grid */}
            <div className="bots-grid">
              <Row className="g-4">
                {botDataArray.map((bot, index) => (
                  <Col key={index} sm={12} md={6} lg={4}>
                    <ModernBotCard bot={bot} index={index} />
                  </Col>
                ))}
              </Row>
            </div>
          </>
        ) : (
          <EmptyState />
        )}
      </Container>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    botData: state.botReducer.botData,
    registeredBotData: state.botReducer.registeredBotData,
    loadingStates: state.botReducer.loadingStates,
    apiCompletionStates: state.botReducer.apiCompletionStates,
    user: state.userReducer.user,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getBotRequest: (payload) => {
      dispatch(getBotRequestAction(payload));
    },
    getRegisteredBotRequest: (payload) => {
      dispatch(getRegisteredBotRequestAction(payload));
    },
    setLoading: (payload) => {
      dispatch(setLoadingAction(payload));
    },
    setLoadingFull: (payload) => {
      dispatch(setLoadingFullAction(payload));
    },
    registerRequest: (creds) => {
      dispatch(registerRequest(creds));
    },
    accountDetailsRequest: (payload) => {
      dispatch(accountDetailsRequestAction(payload));
    },
    resetLoadingStates: () => {
      dispatch(resetLoadingStatesAction());
    }
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(MyBotHistory);